<script lang="ts">
  import type { ForecastPeriod } from '../../api/types'
  import { pairDays, rowMark, weekScale } from '../../forecast/days'

  /**
   * The week as range bars (Phase 10 plan §4.4): a day and its night per row,
   * a bar from the low to the high on the week's own scale, and NWS's short
   * text under it. A row with one end draws a tick and a dash, never a bar
   * to nothing.
   *
   * A tap opens the row's `detailed` text, the day's and the night's, each
   * under its own name. One row is open at a time, and which one is not
   * stored: it is about this forecast, not a preference. The text is
   * interpolated, never `{@html}`; `panels.test.ts` holds that.
   */

  let { forecast, timezone }: { forecast: ForecastPeriod[]; timezone: string | null } = $props()

  const uid = $props.id()

  let rows = $derived(pairDays(forecast, timezone))
  let scale = $derived(weekScale(rows))
  let openKey = $state<string | null>(null)

  const deg = (t: number | null) => (t === null ? '—' : `${t}°`)
</script>

{#if rows.length > 0}
  <section class="week" aria-labelledby="{uid}-title">
    <h2 id="{uid}-title" class="wx-sec-head section-title">7-day forecast</h2>
    <ul>
      {#each rows as row, i (row.key)}
        {@const mark = rowMark(row, scale)}
        {@const open = openKey === row.key}
        <li>
          <button
            type="button"
            class="summary"
            aria-expanded={open}
            aria-controls="{uid}-detail-{i}"
            onclick={() => (openKey = open ? null : row.key)}
          >
            <span class="line">
              <span class="name">{row.name}</span>
              <span class="lo"><span class="visually-hidden">{'Low '}</span>{deg(row.lo)}</span>
              <span class="track" aria-hidden="true">
                {#if mark.kind === 'bar'}
                  <span class="bar" style:left="{mark.left}%" style:width="{mark.width}%"></span>
                {:else if mark.kind === 'tick'}
                  <span class="tick" style:left="{mark.at}%"></span>
                {/if}
              </span>
              <span class="hi"><span class="visually-hidden">{'High '}</span>{deg(row.hi)}</span>
            </span>
            {#if row.text}<span class="text">{row.text}</span>{/if}
          </button>
          <div class="detail" id="{uid}-detail-{i}" hidden={!open}>
            {#if open}
              {#each [row.day, row.night] as period, j (j)}
                {#if period?.detailed}
                  <p class="label">{period.name ?? ''}</p>
                  <p class="detailed">{period.detailed}</p>
                {/if}
              {/each}
            {/if}
          </div>
        </li>
      {/each}
    </ul>
  </section>
{/if}

<style>
  /* Phase 11's section head, set in from the gutter like the rows. */
  .section-title {
    margin: 30px var(--gutter, 0px) 0;
  }

  ul {
    list-style: none;
    margin: 0 var(--gutter, 0px) 20px;
    padding: 0;
  }

  li {
    border-bottom: 1px solid var(--color-divider);
  }

  .summary {
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 13px 0;
    border: 0;
    background: none;
    color: var(--color-text);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  /* Only where there is a hover: on touch it sticks after the tap. */
  @media (hover: hover) {
    .summary:hover .name {
      text-decoration: underline;
      text-decoration-thickness: 2px;
      text-underline-offset: 3px;
    }
  }

  /* The redesign's 104 / 36 / 1fr / 44, narrowed to 96 / 36 / 1fr / 42 so a
     320 px sidebar keeps a bar worth reading: "100°" at 17/800 is 39 px in
     Archivo. Fixed rather than auto, so the bars of every row share one
     track. */
  .line {
    display: grid;
    grid-template-columns: 96px 36px minmax(0, 1fr) 42px;
    gap: 12px;
    align-items: center;
    width: 100%;
  }

  /* A holiday is a period name ("Thanksgiving Day"), so the name wraps
     rather than being cut. */
  .name {
    font-size: 17px;
    font-weight: 800;
    line-height: 1.15;
  }

  .lo,
  .hi {
    font-variant-numeric: tabular-nums;
  }

  .lo {
    font-size: 15px;
    text-align: right;
    color: var(--color-muted);
  }

  .hi {
    font-size: 17px;
    font-weight: 800;
    text-align: right;
  }

  .track {
    position: relative;
    height: 8px;
    background: var(--color-track);
  }

  /* The bar and the tick are marks, so they take the bare accent (3:1 is
     their bar), as 1B draws them. */
  .bar {
    position: absolute;
    top: 0;
    bottom: 0;
    min-width: 4px;
    background: var(--color-accent);
  }

  .tick {
    position: absolute;
    top: -3px;
    bottom: -3px;
    width: 4px;
    margin-left: -2px;
    background: var(--color-accent);
  }

  .text {
    font-size: 14px;
    color: var(--color-muted);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  .detail {
    padding: 0 0 12px;
  }

  .label {
    margin: 6px 0 2px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-muted);
  }

  .detailed {
    margin: 0;
    font-size: 14px;
    line-height: 1.45;
  }
</style>
